/* eslint-disable @typescript-eslint/no-explicit-any */
import { Box, Keyboard, Text, TextInput, TextProps } from 'grommet';
import { WidthType } from 'grommet/utils';
import { useCallback, useEffect, useRef, useState } from 'react';
import grommetTheme from 'src/theme';
import { TalonTip } from '.';

export interface IEditableTextProps {
  val: string;
  onChange: (val: any) => void;
  onBlur?: (val?: any) => void;
  onSubmit?: (val?: any) => void;
  id?: string;
  name?: string;
  minValue?: number;
  maxValue?: number;
  minLength?: number;
  maxLength?: number;
  required?: boolean;
  width?: WidthType;
  textSize?: TextProps['size'];
  textMargin?: TextProps['margin'];
  textColor?: TextProps['color'];
  inputType?: 'text' | 'number' | 'email' | 'password' | 'search' | 'tel' | 'url';
  shortenTo?: number;
  isCurrency?: boolean;
  isPercent?: boolean;
  saveDelay?: number; // Milliseconds to debounce onChange calls
}

export const EditableText = ({
  val,
  onChange,
  onBlur,
  onSubmit,
  id,
  name,
  minValue,
  maxValue,
  minLength,
  maxLength,
  required,
  width,
  textSize,
  textMargin,
  textColor,
  inputType,
  shortenTo,
  isCurrency,
  isPercent,
  saveDelay = 500,
}: IEditableTextProps) => {
  /** State **/
  const [edit, setEdit] = useState<boolean>(false);
  const [value, setValue] = useState<typeof val>(val);

  /** Refs **/
  const inputRef = useRef<HTMLInputElement>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const onChangeRef = useRef(onChange);
  const prevValRef = useRef(val);

  /** Computed **/
  const displayVal =
    value && inputType === 'number' && isCurrency && !Number.isNaN(parseFloat(value))
      ? parseFloat(value).toFixed(2)
      : value;

  /** Methods - Debouncing **/

  // Fire any pending debounced onChanges immediately
  const flush = useCallback(() => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    if (inputRef.current) {
      onChangeRef.current(inputRef.current.value);
    }
  }, []);

  // Schedules a debounced onChange
  const scheduleChange = useCallback(() => {
    if (saveDelay === undefined || saveDelay === 0) {
      // No debounce requested — fire immediately.
      if (inputRef.current) {
        onChangeRef.current(inputRef.current.value);
      }
      return;
    }
    if (timerRef.current !== null) {
      // Reset the timer each keystroke
      clearTimeout(timerRef.current);
    }
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      if (inputRef.current) {
        onChangeRef.current(inputRef.current.value);
      }
    }, saveDelay);
  }, [saveDelay]);

  /** Methods - Event Handlers **/

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.value) {
      const v = typeof e.target.value === 'number' ? (e.target.value as number).toString() : e.target.value;
      if (minLength && v.length < minLength) return;
      if (maxLength && v.length > maxLength) return;
    }
    setValue(e.target.value || '');
    scheduleChange();
  };

  const handleSubmit = () => {
    flush();
    setEdit(false);
    onSubmit && onSubmit();
  };

  const handleBlur = () => {
    flush();
    setEdit(false);
    onBlur && onBlur();
  };

  const takeFocus = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setEdit(true);
  };

  /** Effects **/

  useEffect(() => {
    return () => {
      if (timerRef.current !== null) {
        clearTimeout(timerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (val !== prevValRef.current) {
      setValue(val);
      prevValRef.current = val;
    }
  }, [val, edit]);

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  /** Render **/

  const renderInput = () => (
    <Box width={width} margin={textMargin} alignSelf="start" gap="xxsmall" direction="row" align="center">
      {isCurrency && <Text>$</Text>}
      <Box border={{ side: 'bottom', color: textColor || 'text' }} onBlur={handleBlur}>
        <Keyboard onEnter={handleSubmit}>
          <TextInput
            id={id}
            ref={inputRef}
            name={name || id}
            value={value || ''}
            autoFocus={edit}
            onChange={handleChange}
            onBlur={handleBlur}
            type={inputType}
            required={required}
            min={minValue}
            max={maxValue}
            minLength={minLength}
            maxLength={maxLength}
            plain="full"
            size={textSize}
            width={width}
          />
        </Keyboard>
      </Box>
      {isPercent && <Text color={textColor}>%</Text>}
    </Box>
  );

  const renderText = () => (
    <Box
      width={width}
      margin={textMargin}
      border={{ side: 'bottom', color: textColor || 'text' }}
      alignSelf="start"
      onClick={takeFocus}
      style={{ minHeight: !displayVal ? grommetTheme.text?.medium?.height : undefined, cursor: 'text' }}
      fill={!displayVal}
    >
      {shortenTo ? (
        <TalonTip content={<Text color={textColor}>{displayVal}</Text>}>
          <Text size={textSize} color={textColor}>
            {displayVal.length > shortenTo ? `${displayVal.slice(0, shortenTo - 1)}...` : displayVal}
          </Text>
        </TalonTip>
      ) : (
        <>
          {isCurrency && <Text color={textColor}>$</Text>}
          <Text size={textSize} color={textColor}>
            {displayVal}
          </Text>
          {isPercent && <Text color={textColor}>%</Text>}
        </>
      )}
    </Box>
  );

  return edit ? renderInput() : renderText();
};
